import React, { useEffect, useState } from "react";
import { Box, CircularProgress, Grid, Typography } from "@mui/material";
import { AppLayout } from "layout";
import { DiscoverData } from "modules/Discover/components";
import { useAuth } from "context/authContext";
import { Content as BaseContentType } from "types/types";
import { ContentsService } from "services/ContentsService";

interface ContentWithUser extends BaseContentType {
  userData?: {
    id: string;
    firstName?: string;
    lastName?: string;
  } | null;
}

const DiscoverContainer = () => {
  const { searchTerm } = useAuth();
  const [allContentsData, setAllContentsData] = useState<ContentWithUser[]>([]);
  const [filteredContents, setFilteredContents] = useState<ContentWithUser[]>(
    []
  );
  const [loading, setLoading] = useState<boolean>(true);
  const [isInitialLoad, setIsInitialLoad] = useState(true);

  const fetchContentWithUserData = async () => {
    try {
      // Fetch all contents
      const contents = await ContentsService.getContents();

      // Resolve `createdBy` field (DocumentReference) to user data
      const contentWithUserData = await Promise.all(
        contents.map(async (content: ContentWithUser) => {
          const createdBy = content.createdBy;

          if (!createdBy) {
            console.warn("Invalid createdBy field:", createdBy);
            return { ...content, userData: null };
          }

          try {
            const userData =
              await ContentsService.getUserByCreatedBy(createdBy);
            return {
              ...content,
              userData: userData || null,
            };
          } catch (error) {
            console.error(
              "Error fetching user data for createdBy:",
              createdBy,
              error
            );
            return { ...content, userData: null };
          }
        })
      );

      // ✅ Only keep published content
      const publishedContent = contentWithUserData.filter(
        (content) => content.isPublished
      );

      setAllContentsData(publishedContent);
      setFilteredContents(publishedContent); // Set initial filtered contents

      // setAllContentsData(contentWithUserData);
      // setFilteredContents(contentWithUserData); 
    } catch (error) {
      console.error("Error fetching content with user data:", error);
    } finally {
      setLoading(false);
      setIsInitialLoad(false);
    }
  };

  useEffect(() => {
    fetchContentWithUserData();
  }, []);

  // Filter content based on search term
  useEffect(() => {
    if (!isInitialLoad) {
      // Only filter after initial load
      if (searchTerm.trim() === "") {
        setFilteredContents(allContentsData);
      } else {
        const filtered = allContentsData.filter((content) => {
          const titleMatch = content?.title
            .toLowerCase()
            .includes(searchTerm.toLowerCase());
          const firstNameMatch = content?.userData?.firstName
            ?.toLowerCase()
            .includes(searchTerm.toLowerCase());
          const lastNameMatch = content?.userData?.lastName
            ?.toLowerCase()
            .includes(searchTerm.toLowerCase());

          return titleMatch || firstNameMatch || lastNameMatch;
        });

        setFilteredContents(filtered);
      }
    }
  }, [searchTerm, allContentsData, isInitialLoad]);

  return (
    <AppLayout>
      {loading ? (
        <Box
          justifyContent="center"
          display="flex"
          alignItems="center"
          style={{ minHeight: "100vh" }}
        >
          <CircularProgress sx={{ color: "#233F91" }} />
        </Box>
      ) : !isInitialLoad && filteredContents.length === 0 ? (
        <Box
          justifyContent="center"
          display="flex"
          alignItems="center"
          flexDirection="column"
          gap={2}
          // style={{ minHeight: "50vh" }}
          height="calc(100vh - 64px)" // Ensures full viewport height minus any header
          width="100%" // Ensures full width
          textAlign="center"
        >
          <Typography variant="h5" color="#233F91">
            {searchTerm ? "No results found" : "No data available"}
          </Typography>
        </Box>
      ) : (
        <Box bgcolor={{ xs: "white", md: "white" }}>
          <DiscoverData
            allContentsData={filteredContents}
            setAllContentsData={setFilteredContents}
          />
        </Box>
      )}
    </AppLayout>
  );
};

export default DiscoverContainer;
